import React, { FC, useState, useEffect } from "react";
import { AdminLayout } from "../../../components/layouts/admin-layout/admin-layout";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Colors } from "../../../configs";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { getStationAccessSessionsAction } from "../../../redux/actions/cashierStationActions";
import { CloseStationDrawer } from "../../../components/admin/close-station-drawer/CloseStationDrawer";
import { IStationAccessListItem } from "../../../typings/interfaces/responses/stationAccessResponses";
import { format } from "date-fns";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontWeight: "bold",
    backgroundColor: theme.palette.action.hover,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export const OpenedStations: FC = () => {
  const dispatch = useAppDispatch();
  const { stationAccessSessions, stationAccessSessionsLoading } =
    useAppSelector((state) => state.cashierStation);

  const [statusFilter, setStatusFilter] = useState<string>("open");
  const [dateFilter, setDateFilter] = useState<string>("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSession, setSelectedSession] =
    useState<IStationAccessListItem | null>(null);

  const storeId = 1; // You may want to get this from context or props

  const fetchSessions = () => {
    const params: any = { storeId, status: statusFilter };
    if (dateFilter) {
      params.date = dateFilter;
    }
    dispatch(getStationAccessSessionsAction(params));
  };

  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = () => {
    fetchSessions();
  };

  const handleCloseStation = (session: IStationAccessListItem) => {
    setSelectedSession(session);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedSession(null);
  };

  const handleCloseSuccess = () => {
    fetchSessions(); // Refresh the list after successful close
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "-";
      }
      return format(date, "MMM dd, yyyy hh:mm a");
    } catch (error) {
      return "-";
    }
  };

  const getStatusChip = (status: string) => {
    return (
      <Chip
        label={status.toUpperCase()}
        color={status === "open" ? "warning" : "success"}
        size="small"
      />
    );
  };

  return (
    <AdminLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "2rem",
          marginTop: "4.357rem",
          paddingInline: "5rem",
          width: "100%",
        }}
      >
        <Typography variant="h4" sx={{ color: Colors.TEXT_GRAY_DARK }}>
          Opened Stations
        </Typography>

        {/* Filters Section */}
        <Box
          sx={{
            display: "flex",
            gap: "1.5rem",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <FormControl sx={{ minWidth: "15rem" }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>

          <TextField
            type="date"
            label="Filter by Date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            sx={{ minWidth: "15rem" }}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              max: format(new Date(), "yyyy-MM-dd"),
            }}
          />

          {dateFilter && (
            <Button
              variant="outlined"
              onClick={() => setDateFilter("")}
              sx={{
                minWidth: "8rem",
                height: "3.5rem",
              }}
            >
              Clear Date
            </Button>
          )}

          <Button
            variant="contained"
            onClick={handleFilterChange}
            sx={{
              backgroundColor: Colors.PRIMARY,
              minWidth: "10rem",
              height: "3.5rem",
            }}
          >
            Apply Filters
          </Button>
        </Box>

        {/* Table Section */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            overflow: "auto",
            maxHeight: "calc(100vh - 25rem)",
          }}
        >
          {stationAccessSessionsLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "20rem",
              }}
            >
              <CircularProgress />
            </Box>
          ) : !stationAccessSessions || stationAccessSessions.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "20rem",
              }}
            >
              <Typography variant="body1" color="textSecondary">
                No station sessions found for the selected filters.
              </Typography>
            </Box>
          ) : (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Station Name</StyledTableCell>
                  <StyledTableCell>Opening Time</StyledTableCell>
                  <StyledTableCell>Opened By</StyledTableCell>
                  <StyledTableCell>Closing Time</StyledTableCell>
                  <StyledTableCell>Closed By</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stationAccessSessions.map((session: IStationAccessListItem) => (
                  <StyledTableRow key={session.stationAccessSessionId}>
                    <TableCell>{session.stationName}</TableCell>
                    <TableCell>{formatDateTime(session.openingTime)}</TableCell>
                    <TableCell>{session.openedByName}</TableCell>
                    <TableCell>{formatDateTime(session.closingTime)}</TableCell>
                    <TableCell>{session.closedByName || "-"}</TableCell>
                    <TableCell>{getStatusChip(session.status)}</TableCell>
                    <TableCell>
                      {session.status === "open" && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleCloseStation(session)}
                          sx={{
                            backgroundColor: Colors.PRIMARY,
                            minWidth: "8rem",
                          }}
                        >
                          Close Station
                        </Button>
                      )}
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>

        <CloseStationDrawer
          open={drawerOpen}
          onClose={handleDrawerClose}
          stationAccessSessionId={
            selectedSession?.stationAccessSessionId || null
          }
          stationName={selectedSession?.stationName || ""}
          onSuccess={handleCloseSuccess}
        />
      </Box>
    </AdminLayout>
  );
};

